/** One-request checkout creation shared by the PDP and post-quiz plan. */
(() => {
  if (window.SooidCheckout) return;

  const query = `mutation SooidCheckout($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost { totalAmount { amount currencyCode } }
        lines(first: 2) {
          nodes {
            id
            quantity
            cost { totalAmount { amount currencyCode } }
            merchandise { ... on ProductVariant { id } }
            sellingPlanAllocation { sellingPlan { id } }
          }
        }
      }
      userErrors { message }
      warnings { message }
    }
  }`;

  const gid = (type, id) => `gid://shopify/${type}/${id}`;
  const fail = (message, { retryable = false, fromShopify = false } = {}) => {
    const error = new Error(message || 'We couldn’t start checkout. Please try again.');
    error.retryable = retryable;
    error.fromShopify = fromShopify;
    return error;
  };

  function discountCodes() {
    try {
      return new URLSearchParams(window.location.search).getAll('discount')
        .flatMap(value => value.split(','))
        .map(value => value.trim())
        .filter(Boolean)
        .slice(0, 5);
    } catch {
      return [];
    }
  }

  async function create(item, source) {
    const variantId = Number(item?.id);
    const quantity = Number(item?.quantity);
    const planId = item?.selling_plan ? Number(item.selling_plan) : null;
    if (!Number.isSafeInteger(variantId) || variantId <= 0 || !Number.isInteger(quantity) || quantity <= 0 ||
        (planId !== null && (!Number.isSafeInteger(planId) || planId <= 0))) {
      throw fail();
    }

    const line = { merchandiseId: gid('ProductVariant', variantId), quantity };
    if (planId) line.sellingPlanId = gid('SellingPlan', planId);
    const input = { attributes: [{ key: '_source', value: String(source || 'buy-now') }], lines: [line] };
    // Preserve the Shopify market selected on the storefront in the new cart.
    // This runs before redirecting to Shopify's standard hosted checkout.
    const countryCode = document.documentElement.dataset.sooidCountry;
    if (/^[A-Z]{2}$/.test(countryCode || '')) input.buyerIdentity = { countryCode };
    const discounts = discountCodes();
    if (discounts.length) input.discountCodes = discounts;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    let response;
    let result;
    try {
      response = await fetch('/api/2026-10/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ query, variables: { input } }),
        cache: 'no-store',
        signal: controller.signal,
      });
      try { result = await response.json(); } catch { result = null; }
    } catch (error) {
      throw fail('', { retryable: true });
    } finally {
      clearTimeout(timeout);
    }
    if (!response.ok || !result || result.errors?.length) throw fail('', { retryable: true });

    const payload = result.data?.cartCreate;
    const userError = payload?.userErrors?.find(error => error?.message);
    if (userError) throw fail(userError.message, { fromShopify: true });
    const cart = payload?.cart;
    const lines = cart?.lines?.nodes;
    const chosen = Array.isArray(lines) ? lines[0] : null;
    const expectedVariant = gid('ProductVariant', variantId);
    const expectedPlan = planId ? gid('SellingPlan', planId) : null;
    const actualPlan = chosen?.sellingPlanAllocation?.sellingPlan?.id || null;
    if (!cart?.id || !cart.checkoutUrl || cart.totalQuantity !== quantity || lines?.length !== 1 ||
        chosen?.merchandise?.id !== expectedVariant || chosen?.quantity !== quantity || actualPlan !== expectedPlan) {
      throw fail();
    }

    let checkoutUrl;
    try { checkoutUrl = new URL(cart.checkoutUrl, window.location.origin); } catch { throw fail(); }
    if (checkoutUrl.protocol !== 'https:' && checkoutUrl.origin !== window.location.origin) throw fail();

    return {
      checkoutUrl: checkoutUrl.href,
      cart: {
        id: cart.id,
        totalQuantity: cart.totalQuantity,
        cost: cart.cost,
        lines: lines.map(entry => ({ id: entry.id, quantity: entry.quantity, cost: entry.cost })),
      },
    };
  }

  window.SooidCheckout = { create };
})();
